import { Box, useMediaQuery, useTheme } from "@material-ui/core";
import CloudDownload from "@material-ui/icons/CloudDownload";
import { ContainerPage, TitlePage, Button } from "components";
import { usePopup } from "hooks/usePopup";
import { useTableParams } from "hooks/useTableParams";
import fileDownload from "js-file-download";
import { useCallback } from "react";
import reporterGenerateService from "services/reporterGenerateService";
import storageService from "services/storageService";
import { Table } from "shared";
import { formatQuantity, mapType } from "utils";

import { MobileStockCards } from "./components/MobileStockCards";

// storage-api get_storage_resume filters one distribution center and uses
// GROUP BY product_base.id, returning one row per product (not per purchase type).
const getStorageRowKey = row => row.product_base_id;

export default function Summary() {
  const { data, onChange, params, refresh, status, handleSearch } =
    useTableParams({
      service: params => storageService.getStorageResume(params)
    });
  const { addPopup } = usePopup();
  const theme = useTheme();
  // noSsr: sem isso o primeiro render assume desktop e monta a tabela
  // inteira antes de trocar para os cards — montagem dupla no celular.
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true
  });

  // Aos sábados o backend (scheduledDebitMode="rules") desconta do estoque as
  // saídas agendadas até domingo 23:59; nesses dias exibimos a decomposição.
  const hasScheduledDebits = (data || []).some(
    row => (row.anticipated_scheduled_debits || 0) !== 0
  );

  const columns = [
    {
      field: "product_base_name",
      label: "Produto Base",
      style: { minWidth: "170px", maxWidth: "300px" },
      disabledSort: true
    },
    {
      field: "product_base_id",
      label: "ID do Produto",
      align: "center",
      style: { minWidth: "200px" },
      disabledSort: true
    },
    {
      field: "purchase_type",
      label: "Tipo de entrada",
      align: "center",
      style: { minWidth: "200px" },
      formatter: value => mapType(value),
      disabledSort: true
    },
    ...(hasScheduledDebits
      ? [
          {
            field: "all",
            label: "Saldo atual",
            align: "center",
            style: { minWidth: "160px" },
            formatter: row =>
              formatQuantity(
                row.summary - (row.anticipated_scheduled_debits || 0)
              ),
            cellStyle: row =>
              row.summary - (row.anticipated_scheduled_debits || 0) < 0
                ? { color: "red" }
                : undefined,
            disabledSort: true
          },
          {
            field: "anticipated_scheduled_debits",
            label: "Saídas agendadas (até dom. 23:59)",
            align: "center",
            style: { minWidth: "160px" },
            formatter: value => formatQuantity(value),
            disabledSort: true
          }
        ]
      : []),
    {
      field: "summary",
      label: "Estoque disponível",
      align: "center",
      style: { minWidth: "200px" },
      formatter: value => formatQuantity(value),
      cellStyle: data => {
        if (data.summary < 0) {
          return {
            color: "red"
          };
        }
      },
      disabledSort: true
    }
  ];

  const handleDownloadResume = useCallback(async () => {
    try {
      if (!data?.length) {
        addPopup({
          type: "error",
          description: "Nenhum dado para exportar"
        });
        return;
      }
      const dataToDownload = data.map(row => ({
        ID: row.product_base_id,
        "Produto Base": row.product_base_name,
        "Tipo de entrada": mapType(row.purchase_type),
        "Saldo atual": row.summary - (row.anticipated_scheduled_debits || 0),
        "Saídas agendadas descontadas": row.anticipated_scheduled_debits || 0,
        "Estoque disponível": row.summary
      }));
      const { data: downloadData } =
        await reporterGenerateService.excelGenerate(dataToDownload);

      fileDownload(downloadData, "Dados_do_estoque.xlsx");
    } catch (err) {
      if (err?.response?.status !== 401) {
        addPopup({
          type: "error",
          description: "Erro ao fazer download"
        });
      }
    }
  }, [data, addPopup]);

  return (
    <>
      <ContainerPage>
        {!isMobile && (
          <Box display="flex" justifyContent="space-between" flexWrap="wrap">
            <TitlePage>Estoque resumo</TitlePage>

            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              ml="auto"
              style={{ gap: "1rem" }}
              flexWrap="wrap"
            >
              <Button
                variant="contained"
                startIcon={<CloudDownload />}
                onClick={() => handleDownloadResume()}
              >
                Baixar relatório
              </Button>
            </Box>
          </Box>
        )}

        {isMobile ? (
          <MobileStockCards
            title="Estoque resumo"
            rows={data || []}
            hasScheduledDebits={hasScheduledDebits}
            loading={status === "pending"}
          />
        ) : (
          <Table
            handleSearch={handleSearch}
            columns={columns}
            data={data}
            getRowKey={getStorageRowKey}
            onChange={onChange}
            params={params}
            status={status}
            refresh={refresh}
            count={data?.length}
            size="small"
            hover
            paginationServer={false}
            placeholder="Produto base"
          />
        )}
      </ContainerPage>
    </>
  );
}
